import { Box, CircularProgress, Dialog, DialogActions, DialogContent, TextField as InputField, DialogTitle, FormControl, Grid, Input, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TopToolbar,
    FunctionField,
    DateField,
    Button,
    useDataProvider,
    useRefresh,
    useNotify,
    TextInput,
} from "react-admin";

const STATUS_CHOICES = [
    { id: 1, name: "未对账" },
    { id: 2, name: "审核中" },
    { id: 3, name: "对账失败" },
    { id: 4, name: "已拒绝" },
    { id: 5, name: "对账完成" },
    { id: 6, name: "对账失败" },
    { id: 7, name: "对账完成" },
];

const ListActions = () => {
    return (
        <TopToolbar>
        </TopToolbar>
    );
};


const STATUS_FLOW = {
    2: [3, 4, 5], // 审核中 -> 对账失败, 已拒绝, 对账已完成
    3: [4, 5],    // 对账失败 -> 已拒绝, 对账已完成
    4: [3, 5],    // 已拒绝 -> 对账失败, 对账已完成
    5: []         // 对账已完成 -> 无后续状态
};



export const reconciliationList = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [currentRecord, setCurrentRecord] = useState(null);

    const handleViewDetail = (recordId: any) => {
        setCurrentRecordId(recordId);
        setDetailDialogOpen(true);
    };

    const handleEditRecord = (record: any) => {
        setCurrentRecord(record);
        setEditDialogOpen(true);
    };

    // 关闭详情对话框
    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setCurrentRecordId(null);
    };

    // 关闭编辑对话框
    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setCurrentRecord(null);
    };

    const exportDetails = (record: any) => {
        dataProvider.reconciliationExpor(record)
            .then(() => {
                notify('导出账单详情成功', { type: 'success' });
            })
            .catch((error: any) => {
                notify(error.message || '导出账单详情失败', { type: 'error' });
            });
    };

    return (
        <>
            <List actions={<ListActions />} empty={false}>
                <Datagrid bulkActionButtons={false} rowClick={false}>
                    <TextField source="id" label="ID" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="reconciliationMonth" label="对账月份" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="saleCustomerName" label="销售客户" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="amount" label="对账金额" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    {/* 状态字段 - 使用选择器显示友好名称 */}
                    <FunctionField
                        source="status"
                        label="状态"
                        render={(record) => {
                            const status = STATUS_CHOICES.find(choice => choice.id === record.status);
                            return status ? status.name : record.status;
                        }}
                        sortable={false}
                    />

                    {/* 直接显示日期字段 */}
                    <DateField source="reconciliationDate" label="申请日期" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <DateField source="completionDate" label="完成日期" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    {/* 备注字段 */}
                    <TextField
                        source="remark"
                        label="备注"
                        sortable={false}
                        sx={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            maxWidth: '400px',
                            overflow: 'hidden',
                            display: 'block', // 确保块级显示
                        }}
                    />

                    <FunctionField
                        label="操作"
                        render={(record) => {
                            // 获取可用的状态选项
                            const availableStatusIds = STATUS_FLOW[record.status] || [];
                            const isDisabled = availableStatusIds.length === 0;

                            return (
                                <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                                    <Button
                                        disabled={isDisabled}
                                        label="编辑"
                                        onClick={() => handleEditRecord(record)}
                                    />
                                    <Button label="导出" onClick={() => exportDetails(record.id)} />
                                    <Button
                                        label="详情"
                                        onClick={() => handleViewDetail(record.id)}
                                    />
                                </Box>
                            );
                        }}
                        sortable={false}
                    />
                </Datagrid>
            </List>
            <DetailDialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                recordId={currentRecordId}
            />
            <EditDialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                record={currentRecord}
            />
        </>
    )
};

// 详情对话框组件
const DetailDialog = ({ open, onClose, recordId }) => {
    const dataProvider = useDataProvider();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && recordId) {
            const fetchDetail = async () => {
                try {
                    setIsLoading(true);
                    setError(null);
                    const result = await dataProvider.reconciliationBill(recordId);
                    setData(result);
                } catch (error) {
                    console.error("获取对账单详情失败:", error);
                    setError(error);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchDetail();
        }
    }, [open, recordId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>对账单详情</DialogTitle>
            <DialogContent>
                {isLoading && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box display="flex" justifyContent="center" my={4}>
                        <Typography color="error">加载详情失败: {error.message}</Typography>
                    </Box>
                )}

                {data && !isLoading && !error && (
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold">ID</Typography>
                                <Typography>{data.billNo}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold">对账月份</Typography>
                                <Typography>{data.reconciliationMonth}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold">销售客户</Typography>
                                <Typography>{data.saleCustomerName}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold">对账金额</Typography>
                                <Typography>{data.totalAmount} 元</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" fontWeight="bold" mt={2} mb={1}>
                                    交易明细 ({data.details?.length || 0}条)
                                </Typography>

                                {data.details?.length > 0 ? (
                                    <Box>
                                        {data.details.map((detail, index) => (
                                            <Box
                                                key={index}
                                                mb={2}
                                                p={2}
                                                border={1}
                                                borderColor="divider"
                                                borderRadius={1}
                                                bgcolor="background.paper"
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="subtitle2" fontWeight="bold">日期</Typography>
                                                        <Typography>{detail.dataDate}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="subtitle2" fontWeight="bold">收入</Typography>
                                                        <Typography>{detail.amount} 元</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">无交易明细</Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">关闭</Button>
            </DialogActions>
        </Dialog>
    );
};

// 编辑对话框组件
const EditDialog = ({ open, onClose, record }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [formData, setFormData] = useState({
        reconciliationMonth: '',
        saleCustomerId: '',
        status: '',
        remark: ''
    });
    const [customerTypeChoices, setSaleCustomers] = useState([]);

    useEffect(() => {
        if (record) {
            setFormData({
                reconciliationMonth: record.reconciliationMonth || '',
                saleCustomerId: record.saleCustomerId || '',
                status: record.status || '',
                remark: record.remark || ''
            });
        }
    }, [record]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const salesData = await dataProvider.getSalesCustomers()
                setSaleCustomers(salesData.map(item => ({ id: item.id, name: item.name })));
            } catch (error) {
                console.error("获取客户数据失败:", error);
            }
        };
        fetchCustomers();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!record) return;

        try {
            // 调用更新API
            await dataProvider.update('reconciliation', {
                id: record.id,
                data: {
                    ...formData,
                    salesCustomerId: Number(formData.saleCustomerId)
                }
            });

            notify('对账单更新成功', { type: 'success' });
            onClose();
            refresh();
        } catch (error) {
            console.error("更新对账单失败:", error);
            notify('更新对账单失败: ' + error.message, { type: 'error' });
        }
    };

    const getAvailableStatusChoices = () => {
        if (!record) return [];

        const currentStatus = record.status;
        const availableStatusIds = STATUS_FLOW[currentStatus] || [];

        return STATUS_CHOICES.filter(choice =>
            availableStatusIds.includes(choice.id)
        );
    };

    const availableChoices = getAvailableStatusChoices();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>编辑对账单</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <Typography variant="subtitle1">
                        <strong>对账月份:</strong> {formData.reconciliationMonth}
                    </Typography>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel>销售客户名称</InputLabel>
                    <Select
                        name="saleCustomerId"
                        disabled
                        value={formData.saleCustomerId}
                        onChange={handleInputChange}
                        label="销售客户名称"
                    >
                        {customerTypeChoices.map(choice => (
                            <MenuItem key={choice.id} value={choice.id}>
                                {choice.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel>状态</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        label="状态"
                        disabled={availableChoices.length === 0}
                    >
                        {availableChoices.map(choice => (
                            <MenuItem key={choice.id} value={choice.id}>
                                {choice.name}
                            </MenuItem>
                        ))}
                        {availableChoices.length === 0 && (
                            <MenuItem value="" disabled>
                                无可用状态选项
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputField
                        label="备注"
                        name="remark"
                        value={formData.remark}
                        onChange={handleInputChange}
                        multiline
                        rows={4}  // 默认显示4行
                        fullWidth
                        inputProps={{
                            maxLength: 500  // 最大500字符限制
                        }}
                        helperText={`${formData.remark.length}/500`}  // 显示当前字符数/最大限制
                        FormHelperTextProps={{
                            style: { textAlign: 'right' }  // 右对齐计数器
                        }}
                    />
                </FormControl>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">取消</Button>
                <Button onClick={handleSubmit} color="primary">保存</Button>
            </DialogActions>
        </Dialog >
    );
};